import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid2,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Alert,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    PlayArrow as StartIcon,
    Schedule as TimeIcon,
    Security as SecurityIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import {
    WORKFLOW_TEMPLATES,
    WorkflowTemplate,
    getWorkflowsByCategory,
    getWorkflowsByDifficulty,
    searchWorkflows
} from '../../utils/workflowTemplates';
import WorkflowExecutor from './WorkflowExecutor';
import logger from '../../utils/logger';

interface WorkflowTemplateSelectorProps {
    onSelectTemplate: (template: WorkflowTemplate) => void;
    onClose?: () => void;
}

const WorkflowTemplateSelector: React.FC<WorkflowTemplateSelectorProps> = ({
    onSelectTemplate,
    onClose
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [executingTemplate, setExecutingTemplate] = useState<WorkflowTemplate | null>(null);

    // Mock workflow context - in a real app this would come from a provider
    const workflowContext = {
        activeWorkflows: [],
        registerWorkflow: (type: string) => `workflow_${type}_${Date.now()}`,
        completeWorkflow: (key: string) => {
            logger.log('Workflow completed:', key);
        }
    };

    // Filter templates based on search and filters
    const getFilteredTemplates = (): WorkflowTemplate[] => {
        let templates = WORKFLOW_TEMPLATES;

        // Apply search filter
        if (searchQuery.trim()) {
            templates = searchWorkflows(searchQuery);
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            templates = templates.filter(t => t.category === categoryFilter);
        }

        // Apply difficulty filter
        if (difficultyFilter !== 'all') {
            templates = templates.filter(t => t.difficulty === difficultyFilter);
        }

        return templates;
    };

    const filteredTemplates = getFilteredTemplates();

    // Get difficulty color
    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'beginner': return 'success';
            case 'intermediate': return 'warning';
            case 'advanced': return 'error';
            default: return 'default';
        }
    };

    // Get category icon
    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'governance': return <SecurityIcon />;
            case 'token': return <CheckIcon />;
            case 'defi': return <InfoIcon />;
            case 'utility': return <WarningIcon />;
            default: return <InfoIcon />;
        }
    };

    // Handle template selection
    const handleSelectTemplate = (template: WorkflowTemplate) => {
        setSelectedTemplate(template);
        setDetailsDialogOpen(true);
    };

    // Handle start workflow
    const handleStartWorkflow = () => {
        if (selectedTemplate) {
            logger.log('Starting workflow:', selectedTemplate.id);
            onSelectTemplate(selectedTemplate);
            setDetailsDialogOpen(false);
            if (onClose) onClose();
        }
    };

    // Render template card
    const renderTemplateCard = (template: WorkflowTemplate) => (
        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={template.id}>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                    }
                }}
            >
                <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {getCategoryIcon(template.category)}
                        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                            {template.name}
                        </Typography>
                        <Chip
                            label={template.difficulty}
                            size="small"
                            color={getDifficultyColor(template.difficulty) as any}
                            variant="outlined"
                        />
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                    </Typography>

                    {/* Metadata */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TimeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        <Typography variant="caption" sx={{ mr: 2 }}>
                            ~{template.estimatedTime} min
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {template.steps.length} steps
                        </Typography>
                    </Box>

                    {/* Tags */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {template.tags.slice(0, 3).map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                            />
                        ))}
                        {template.tags.length > 3 && (
                            <Chip
                                label={`+${template.tags.length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                    </Box>

                    {/* Progress indicator */}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Workflow Steps
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={0}
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                        />
                    </Box>
                </CardContent>

                <CardActions>
                    <Button
                        size="small"
                        onClick={() => handleSelectTemplate(template)}
                        startIcon={<InfoIcon />}
                    >
                        Details
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelectTemplate(template)}
                        startIcon={<StartIcon />}
                        sx={{ ml: 'auto' }}
                    >
                        Start
                    </Button>
                </CardActions>
            </Card>
        </Grid2>
    );

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Workflow Templates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Choose a pre-configured workflow to automate complex multi-step operations
                </Typography>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid2 container spacing={2} alignItems="center">
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search workflows..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Category"
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Categories</MenuItem>
                                <MenuItem value="governance">Governance</MenuItem>
                                <MenuItem value="token">Token</MenuItem>
                                <MenuItem value="defi">DeFi</MenuItem>
                                <MenuItem value="utility">Utility</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={difficultyFilter}
                                label="Difficulty"
                                onChange={(e) => setDifficultyFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Levels</MenuItem>
                                <MenuItem value="beginner">Beginner</MenuItem>
                                <MenuItem value="intermediate">Intermediate</MenuItem>
                                <MenuItem value="advanced">Advanced</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            {filteredTemplates.length} workflow{filteredTemplates.length !== 1 ? 's' : ''} found
                        </Typography>
                    </Grid2>
                </Grid2>
            </Paper>

            {/* Templates Grid */}
            {filteredTemplates.length > 0 ? (
                <Grid2 container spacing={3}>
                    {filteredTemplates.map(renderTemplateCard)}
                </Grid2>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <FilterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No workflows found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try adjusting your search criteria or filters
                    </Typography>
                </Paper>
            )}

            {/* Template Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {selectedTemplate && getCategoryIcon(selectedTemplate.category)}
                        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                            {selectedTemplate?.name}
                        </Typography>
                        <Chip
                            label={selectedTemplate?.difficulty}
                            size="small"
                            color={getDifficultyColor(selectedTemplate?.difficulty || '') as any}
                            variant="outlined"
                        />
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {selectedTemplate && (
                        <Box>
                            {/* Description */}
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                {selectedTemplate.description}
                            </Typography>

                            {/* Metadata */}
                            <Grid2 container spacing={2} sx={{ mb: 3 }}>
                                <Grid2 size={{ xs: 6 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TimeIcon sx={{ mr: 1 }} />
                                        <Typography variant="body2">
                                            Estimated Time: {selectedTemplate.estimatedTime} minutes
                                        </Typography>
                                    </Box>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2">
                                        Steps: {selectedTemplate.steps.length}
                                    </Typography>
                                </Grid2>
                            </Grid2>

                            {/* Prerequisites */}
                            {selectedTemplate.prerequisites && selectedTemplate.prerequisites.length > 0 && (
                                <Accordion sx={{ mb: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">
                                            Prerequisites ({selectedTemplate.prerequisites.length})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <List dense>
                                            {selectedTemplate.prerequisites.map((prereq, index) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <CheckIcon color="success" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={prereq} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Security Notes */}
                            {selectedTemplate.securityNotes && selectedTemplate.securityNotes.length > 0 && (
                                <Accordion sx={{ mb: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">
                                            Security Notes ({selectedTemplate.securityNotes.length})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <List dense>
                                            {selectedTemplate.securityNotes.map((note, index) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <SecurityIcon color="warning" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={note} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Workflow Steps */}
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">
                                        Workflow Steps ({selectedTemplate.steps.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <List>
                                        {selectedTemplate.steps.map((step, index) => (
                                            <React.Fragment key={step.id}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                bgcolor: 'primary.main',
                                                                color: 'white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={step.title}
                                                        secondary={
                                                            <React.Fragment>
                                                                <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                                                    {step.description}
                                                                </span>
                                                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                                                    ~{Math.round((step.estimatedTime || 0) / 60)} min
                                                                </span>
                                                            </React.Fragment>
                                                        }
                                                    />
                                                </ListItem>
                                                {index < selectedTemplate.steps.length - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>

                            {/* Warning */}
                            <Alert severity="info" sx={{ mt: 2 }}>
                                This workflow will guide you through each step with automated data passing between steps.
                                You can pause and resume at any time.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStartWorkflow}
                        startIcon={<StartIcon />}
                    >
                        Start Workflow
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkflowTemplateSelector;
